import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/shared/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { Switch } from '@/shared/components/ui/switch';
import { toast } from 'sonner';
import {
  Plus, Search, Globe, Settings2, LayoutGrid, DollarSign,
  Eye, Edit, Trash2, CheckCircle, XCircle, ExternalLink,
  Building2, Image, Tag, Loader2 } from
'lucide-react';


export default function PortalManager() {const { t } = useTranslation("crm");
  const [activeTab, setActiveTab] = useState('portals');
  const [portals, setPortals] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [specSets, setSpecSets] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPortal, setSelectedPortal] = useState(null);

  // Modal states
  const [showPortalModal, setShowPortalModal] = useState(false);
  const [showPlacementModal, setShowPlacementModal] = useState(false);
  const [showSpecSetModal, setShowSpecSetModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [portalsRes, placementsRes, specSetsRes, marketsRes] = await Promise.all([
      fetch(`${API_BASE}/api/portal-registry/portals`),
      fetch(`${API_BASE}/api/portal-registry/placements`),
      fetch(`${API_BASE}/api/portal-registry/spec-sets`),
      fetch(`${API_BASE}/api/portal-registry/markets`)]
      );

      const portalsData = await portalsRes.json();
      const placementsData = await placementsRes.json();
      const specSetsData = await specSetsRes.json();
      const marketsData = await marketsRes.json();

      setPortals(Array.isArray(portalsData) ? portalsData : []);
      setPlacements(Array.isArray(placementsData) ? placementsData : []);
      setSpecSets(Array.isArray(specSetsData) ? specSetsData : []);
      setMarkets(Array.isArray(marketsData) ? marketsData : []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load portal data');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredPortals = portals.filter((p) =>
  p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
  p.display_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredPlacements = placements.filter((p) =>
  p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
  p.portal_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredSpecSets = specSets.filter((s) =>
  s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
  s.code?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSavePortal = async (formData) => {
    try {
      const url = editingItem ?
      `${API_BASE}/api/portal-registry/portals/${editingItem.id}` :
      `${API_BASE}/api/portal-registry/portals`;

      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to save portal');

      toast.success(editingItem ? 'Portal updated' : 'Portal created');
      setShowPortalModal(false);
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSavePlacement = async (formData) => {
    try {
      const url = editingItem ?
      `${API_BASE}/api/portal-registry/placements/${editingItem.id}` :
      `${API_BASE}/api/portal-registry/placements`;

      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to save placement');

      toast.success(editingItem ? 'Placement updated' : 'Placement created');
      setShowPlacementModal(false);
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSaveSpecSet = async (formData) => {
    try {
      const url = editingItem ?
      `${API_BASE}/api/portal-registry/spec-sets/${editingItem.id}` :
      `${API_BASE}/api/portal-registry/spec-sets`;

      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to save spec set');

      toast.success(editingItem ? 'Spec set updated' : 'Spec set created');
      setShowSpecSetModal(false);
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeletePortal = async (id) => {
    if (!window.confirm('Are you sure you want to delete this portal?')) return;
    try {
      await fetch(`${API_BASE}/api/portal-registry/portals/${id}`, { method: 'DELETE' });
      toast.success('Portal deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete portal');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'bg-green-500/10 text-green-500 border-green-500/20',
      inactive: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
      pending: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20'
    };
    return (
      <Badge variant="outline" className={variants[status] || variants.inactive}>
        {status}
      </Badge>);

  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>);

  }

  return (
    <div className="p-6 space-y-6" data-testid="portal-manager">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t("portalManager", "Portal Manager")}</h1>
          <p className="text-sm text-gray-500 mt-1">{t("managePublisherPortals", "Manage publisher portals, ad placements, and creative specifications")}

          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-64"
              data-testid="portal-search" />
            
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-900/20 dark:to-blue-800/10 border-blue-200/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Globe className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t("portals", "Portals")}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{portals.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100/50 dark:from-purple-900/20 dark:to-purple-800/10 border-purple-200/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded-lg">
                <LayoutGrid className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t("placements", "Placements")}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{placements.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100/50 dark:from-green-900/20 dark:to-green-800/10 border-green-200/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <Settings2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t("specSets", "Spec Sets")}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{specSets.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50 dark:from-amber-900/20 dark:to-amber-800/10 border-amber-200/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/10 rounded-lg">
                <Building2 className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t("markets", "Markets")}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{markets.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-gray-100 dark:bg-gray-800 p-1">
          <TabsTrigger value="portals" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
            <Globe className="w-4 h-4 mr-2" />{t("portals", "Portals")}

          </TabsTrigger>
          <TabsTrigger value="placements" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
            <LayoutGrid className="w-4 h-4 mr-2" />{t("placements", "Placements")}

          </TabsTrigger>
          <TabsTrigger value="spec-sets" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
            <Settings2 className="w-4 h-4 mr-2" />{t("specSets", "Spec Sets")}

          </TabsTrigger>
        </TabsList>

        {/* Portals Tab */}
        <TabsContent value="portals" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => {setEditingItem(null);setShowPortalModal(true);}} data-testid="add-portal-btn">
              <Plus className="w-4 h-4 mr-2" />{t("addPortal", "Add Portal")}

            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPortals.map((portal) =>
            <Card key={portal.id} className="hover:shadow-lg transition-shadow" data-testid={`portal-card-${portal.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {portal.logo_url ?
                    <img src={portal.logo_url} alt={portal.name} className="w-10 h-10 rounded-lg object-cover" /> :

                    <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                          <Globe className="w-5 h-5 text-blue-600" />
                        </div>
                    }
                      <div>
                        <CardTitle className="text-base">{portal.display_name || portal.name}</CardTitle>
                        <p className="text-xs text-gray-500">{portal.slug}</p>
                      </div>
                    </div>
                    {getStatusBadge(portal.status)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex flex-wrap gap-1">
                    {portal.vertical_tags?.slice(0, 3).map((tag, i) =>
                  <Badge key={i} variant="secondary" className="text-xs">{tag}</Badge>
                  )}
                    {portal.vertical_tags?.length > 3 &&
                  <Badge variant="secondary" className="text-xs">+{portal.vertical_tags.length - 3}</Badge>
                  }
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div className="bg-gray-50 dark:bg-gray-800 rounded p-2">
                      <p className="font-semibold text-gray-900 dark:text-white">{portal.placement_count || 0}</p>
                      <p className="text-gray-500">{t("placements", "Placements")}</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800 rounded p-2">
                      <p className="font-semibold text-gray-900 dark:text-white">{portal.rate_card_count || 0}</p>
                      <p className="text-gray-500">{t("rateCards", "Rate Cards")}</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800 rounded p-2">
                      <p className="font-semibold text-gray-900 dark:text-white">{portal.markets_supported?.length || 0}</p>
                      <p className="text-gray-500">{t("markets", "Markets")}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t">
                    <Badge variant="outline" className="text-xs">
                      {portal.mode_api_enabled && <span className="text-green-500">A</span>}
                      {portal.mode_direct_io_enabled && <span className="text-blue-500 ml-1">B</span>}
                      {portal.mode_self_serve_enabled && <span className="text-purple-500 ml-1">C</span>}
                    </Badge>
                    <span className="text-xs text-gray-500 flex-1">{portal.portal_type}</span>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => {setEditingItem(portal);setShowPortalModal(true);}}>
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDeletePortal(portal.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Placements Tab */}
        <TabsContent value="placements" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => {setEditingItem(null);setShowPlacementModal(true);}} data-testid="add-placement-btn">
              <Plus className="w-4 h-4 mr-2" />{t("addPlacement", "Add Placement")}

            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="placements-table">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{t("placement", "Placement")}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{t("portal", "Portal")}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{t("type", "Type")}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{t("position", "Position")}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{t("specSet", "Spec Set")}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{t("status", "Status")}</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400">{t("actions", "Actions")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {filteredPlacements.map((placement) =>
                    <tr key={placement.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900 dark:text-white">{placement.name}</div>
                          <div className="text-xs text-gray-500">{placement.slug}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{placement.portal_name}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline">{placement.placement_type}</Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{placement.position}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{placement.spec_set_code || '-'}</td>
                        <td className="px-4 py-3">{getStatusBadge(placement.is_active ? 'active' : 'inactive')}</td>
                        <td className="px-4 py-3 text-right">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => {setEditingItem(placement);setShowPlacementModal(true);}}>
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      </tr>
                    )}
                    {filteredPlacements.length === 0 &&
                    <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-gray-500">{t("noPlacementsFound", "No placements found")}</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Spec Sets Tab */}
        <TabsContent value="spec-sets" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => {setEditingItem(null);setShowSpecSetModal(true);}} data-testid="add-spec-set-btn">
              <Plus className="w-4 h-4 mr-2" />{t("addSpecSet", "Add Spec Set")}

            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSpecSets.map((spec) =>
            <Card key={spec.id} className="hover:shadow-lg transition-shadow" data-testid={`spec-set-card-${spec.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-base">{spec.name}</CardTitle>
                      <p className="text-xs text-gray-500 font-mono">{spec.code}</p>
                    </div>
                    <Badge variant={spec.is_global ? "default" : "secondary"}>
                      {spec.is_global ? 'Global' : 'Custom'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-gray-50 dark:bg-gray-800 rounded p-2">
                      <p className="text-gray-500">{t("mediaType", "Media Type")}</p>
                      <p className="font-medium text-gray-900 dark:text-white capitalize">{spec.media_type || 'image'}</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800 rounded p-2">
                      <p className="text-gray-500">{t("maxSize", "Max Size")}</p>
                      <p className="font-medium text-gray-900 dark:text-white">{spec.max_file_size_kb ? `${spec.max_file_size_kb}KB` : '-'}</p>
                    </div>
                  </div>
                  {Array.isArray(spec.allowed_sizes) && spec.allowed_sizes.length > 0 &&
                <div>
                      <p className="text-xs text-gray-500 mb-1">{t("sizes", "Sizes")}</p>
                      <div className="flex flex-wrap gap-1">
                        {spec.allowed_sizes.slice(0, 4).map((size, i) =>
                    <Badge key={i} variant="outline" className="text-xs font-mono">{size}</Badge>
                    )}
                        {spec.allowed_sizes.length > 4 &&
                    <Badge variant="outline" className="text-xs">+{spec.allowed_sizes.length - 4}</Badge>
                    }
                      </div>
                    </div>
                }
                  {spec.headline_max_chars &&
                <div className="text-xs text-gray-500">{t("headline", "Headline:")}
                  {spec.headline_min_chars || 0}-{spec.headline_max_chars}{t("chars", "chars")}
                </div>
                }
                  <div className="flex justify-end gap-2 pt-2 border-t">
                    <Button variant="ghost" size="sm" onClick={() => {setEditingItem(spec);setShowSpecSetModal(true);}}>
                      <Edit className="w-3.5 h-3.5 mr-1" />{t("edit", "Edit")}

                  </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Portal Modal */}
      <PortalFormModal
        open={showPortalModal}
        onClose={() => {setShowPortalModal(false);setEditingItem(null);}}
        onSave={handleSavePortal}
        portal={editingItem}
        markets={markets} />
      

      {/* Placement Modal */}
      <PlacementFormModal
        open={showPlacementModal}
        onClose={() => {setShowPlacementModal(false);setEditingItem(null);}}
        onSave={handleSavePlacement}
        placement={editingItem}
        portals={portals}
        specSets={specSets} />
      

      {/* Spec Set Modal */}
      <SpecSetFormModal
        open={showSpecSetModal}
        onClose={() => {setShowSpecSetModal(false);setEditingItem(null);}}
        onSave={handleSaveSpecSet}
        specSet={editingItem} />
      
    </div>);

}

// Portal Form Modal
function PortalFormModal({ open, onClose, onSave, portal, markets }) {const { t } = useTranslation("crm");
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    slug: '',
    portal_type: 'network',
    vertical_tags: [],
    markets_supported: [],
    default_currency: 'USD',
    mode_api_enabled: false,
    mode_direct_io_enabled: true,
    mode_self_serve_enabled: true,
    status: 'active'
  });

  useEffect(() => {
    if (portal) {
      setFormData({
        name: portal.name || '',
        display_name: portal.display_name || '',
        slug: portal.slug || '',
        portal_type: portal.portal_type || 'network',
        vertical_tags: portal.vertical_tags || [],
        markets_supported: portal.markets_supported || [],
        default_currency: portal.default_currency || 'USD',
        mode_api_enabled: portal.mode_api_enabled || false,
        mode_direct_io_enabled: portal.mode_direct_io_enabled ?? true,
        mode_self_serve_enabled: portal.mode_self_serve_enabled ?? true,
        status: portal.status || 'active'
      });
    } else {
      setFormData({
        name: '',
        display_name: '',
        slug: '',
        portal_type: 'network',
        vertical_tags: [],
        markets_supported: [],
        default_currency: 'USD',
        mode_api_enabled: false,
        mode_direct_io_enabled: true,
        mode_self_serve_enabled: true,
        status: 'active'
      });
    }
  }, [portal, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{portal ? 'Edit Portal' : 'Add New Portal'}</DialogTitle>
          <DialogDescription>{t("configureThePortal", "Configure the portal details and publishing modes.")}

          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("name", "Name *")}</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="MSN"
                required />
              
            </div>
            <div className="space-y-2">
              <Label>{t("displayName", "Display Name")}</Label>
              <Input
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                placeholder="MSN Portal" />
              
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("slug", "Slug")}</Label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="msn" />
              
            </div>
            <div className="space-y-2">
              <Label>{t("type", "Type")}</Label>
              <Select value={formData.portal_type} onValueChange={(v) => setFormData({ ...formData, portal_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="network">{t("network", "Network")}</SelectItem>
                  <SelectItem value="news">{t("news", "News")}</SelectItem>
                  <SelectItem value="magazine">{t("magazine", "Magazine")}</SelectItem>
                  <SelectItem value="specialized">{t("specialized", "Specialized")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t("verticalTagsCommaseparated", "Vertical Tags (comma-separated)")}</Label>
            <Input
              value={formData.vertical_tags.join(', ')}
              onChange={(e) => setFormData({ ...formData, vertical_tags: e.target.value.split(',').map((t) => t.trim()).filter(Boolean) })}
              placeholder="logistics, supply_chain, wms" />
            
          </div>
          <div className="space-y-3 border rounded-lg p-3">
            <p className="text-sm font-medium">{t("publishingModes", "Publishing Modes")}</p>
            <div className="flex items-center justify-between">
              <Label className="text-sm font-normal">{t("modeAApinetwork", "Mode A: API/Network")}</Label>
              <Switch
                checked={formData.mode_api_enabled}
                onCheckedChange={(v) => setFormData({ ...formData, mode_api_enabled: v })} />
              
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm font-normal">{t("modeBDirect", "Mode B: Direct IO")}</Label>
              <Switch
                checked={formData.mode_direct_io_enabled}
                onCheckedChange={(v) => setFormData({ ...formData, mode_direct_io_enabled: v })} />
              
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm font-normal">{t("modeCSelfserve", "Mode C: Self-Serve")}</Label>
              <Switch
                checked={formData.mode_self_serve_enabled}
                onCheckedChange={(v) => setFormData({ ...formData, mode_self_serve_enabled: v })} />
              
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>{t("cancel", "Cancel")}</Button>
            <Button type="submit">{portal ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>);

}

// Placement Form Modal
function PlacementFormModal({ open, onClose, onSave, placement, portals, specSets }) {const { t } = useTranslation("crm");
  const [formData, setFormData] = useState({
    portal_id: '',
    name: '',
    slug: '',
    placement_type: 'display',
    position: '',
    section: '',
    spec_set_id: '',
    is_active: true
  });

  useEffect(() => {
    if (placement) {
      setFormData({
        portal_id: placement.portal_id?.toString() || '',
        name: placement.name || '',
        slug: placement.slug || '',
        placement_type: placement.placement_type || 'display',
        position: placement.position || '',
        section: placement.section || '',
        spec_set_id: placement.spec_set_id?.toString() || '',
        is_active: placement.is_active ?? true
      });
    } else {
      setFormData({
        portal_id: '',
        name: '',
        slug: '',
        placement_type: 'display',
        position: '',
        section: '',
        spec_set_id: '',
        is_active: true
      });
    }
  }, [placement, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      portal_id: parseInt(formData.portal_id),
      spec_set_id: formData.spec_set_id ? parseInt(formData.spec_set_id) : null
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{placement ? 'Edit Placement' : 'Add New Placement'}</DialogTitle>
          <DialogDescription>{t("defineAnAd", "Define an ad placement slot within a portal.")}

          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>{t("portal", "Portal *")}</Label>
            <Select value={formData.portal_id} onValueChange={(v) => setFormData({ ...formData, portal_id: v })} required>
              <SelectTrigger>
                <SelectValue placeholder="Select portal" />
              </SelectTrigger>
              <SelectContent>
                {portals.map((p) =>
                <SelectItem key={p.id} value={p.id.toString()}>{p.display_name || p.name}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("name", "Name *")}</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Homepage Hero"
                required />
              
            </div>
            <div className="space-y-2">
              <Label>{t("slug", "Slug")}</Label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="homepage-hero" />
              
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("type", "Type")}</Label>
              <Select value={formData.placement_type} onValueChange={(v) => setFormData({ ...formData, placement_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="display">{t("display", "Display")}</SelectItem>
                  <SelectItem value="native">{t("native", "Native")}</SelectItem>
                  <SelectItem value="video">{t("video", "Video")}</SelectItem>
                  <SelectItem value="sponsored">{t("sponsoredContent", "Sponsored Content")}</SelectItem>
                  <SelectItem value="newsletter">{t("newsletter", "Newsletter")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("specSet", "Spec Set")}</Label>
              <Select value={formData.spec_set_id} onValueChange={(v) => setFormData({ ...formData, spec_set_id: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select spec set" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">{t("none", "None")}</SelectItem>
                  {specSets.map((s) =>
                  <SelectItem key={s.id} value={s.id.toString()}>{s.name} ({s.code})</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("position", "Position")}</Label>
              <Input
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                placeholder="above_fold, sidebar" />
              
            </div>
            <div className="space-y-2">
              <Label>{t("section", "Section")}</Label>
              <Input
                value={formData.section}
                onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                placeholder="homepage, article" />
              
            </div>
          </div>
          <div className="flex items-center justify-between">
            <Label>{t("active", "Active")}</Label>
            <Switch checked={formData.is_active} onCheckedChange={(v) => setFormData({ ...formData, is_active: v })} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>{t("cancel", "Cancel")}</Button>
            <Button type="submit">{placement ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>);

}

// Spec Set Form Modal
function SpecSetFormModal({ open, onClose, onSave, specSet }) {const { t } = useTranslation("crm");
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    media_type: 'image',
    allowed_sizes: [],
    allowed_formats: ['jpg', 'png'],
    max_file_size_kb: 500,
    headline_max_chars: 60,
    headline_min_chars: 10,
    body_text_max_chars: 150,
    body_text_min_chars: 20,
    cta_max_chars: 25,
    is_global: false
  });

  useEffect(() => {
    if (specSet) {
      setFormData({
        name: specSet.name || '',
        code: specSet.code || '',
        media_type: specSet.media_type || 'image',
        allowed_sizes: specSet.allowed_sizes || [],
        allowed_formats: specSet.allowed_formats || ['jpg', 'png'],
        max_file_size_kb: specSet.max_file_size_kb || 500,
        headline_max_chars: specSet.headline_max_chars || 60,
        headline_min_chars: specSet.headline_min_chars || 10,
        body_text_max_chars: specSet.body_text_max_chars || 150,
        body_text_min_chars: specSet.body_text_min_chars || 20,
        cta_max_chars: specSet.cta_max_chars || 25,
        is_global: specSet.is_global || false
      });
    } else {
      setFormData({
        name: '',
        code: '',
        media_type: 'image',
        allowed_sizes: [],
        allowed_formats: ['jpg', 'png'],
        max_file_size_kb: 500,
        headline_max_chars: 60,
        headline_min_chars: 10,
        body_text_max_chars: 150,
        body_text_min_chars: 20,
        cta_max_chars: 25,
        is_global: false
      });
    }
  }, [specSet, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{specSet ? 'Edit Spec Set' : 'Add New Spec Set'}</DialogTitle>
          <DialogDescription>{t("defineCreativeSpecifications", "Define creative specifications for ad placements.")}

          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("name", "Name *")}</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Standard Display"
                required />
              
            </div>
            <div className="space-y-2">
              <Label>{t("code", "Code *")}</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="STD_DISPLAY"
                required />
              
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("mediaType", "Media Type")}</Label>
              <Select value={formData.media_type} onValueChange={(v) => setFormData({ ...formData, media_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="image">{t("image", "Image")}</SelectItem>
                  <SelectItem value="video">{t("video", "Video")}</SelectItem>
                  <SelectItem value="html5">{t("html5", "HTML5")}</SelectItem>
                  <SelectItem value="native">{t("native", "Native")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("maxFileSize", "Max File Size (KB)")}</Label>
              <Input
                type="number"
                value={formData.max_file_size_kb}
                onChange={(e) => setFormData({ ...formData, max_file_size_kb: parseInt(e.target.value) })} />
              
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t("allowedSizesCommaseparated", "Allowed Sizes (comma-separated)")}</Label>
            <Input
              value={formData.allowed_sizes.join(', ')}
              onChange={(e) => setFormData({ ...formData, allowed_sizes: e.target.value.split(',').map((t) => t.trim()).filter(Boolean) })}
              placeholder="300x250, 728x90, 160x600" />
            
          </div>
          <div className="space-y-2">
            <Label>{t("allowedFormatsCommaseparated", "Allowed Formats (comma-separated)")}</Label>
            <Input
              value={formData.allowed_formats.join(', ')}
              onChange={(e) => setFormData({ ...formData, allowed_formats: e.target.value.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean) })}
              placeholder="jpg, png, gif" />
            
          </div>
          <div className="border rounded-lg p-3 space-y-3">
            <p className="text-sm font-medium">{t("textLimits", "Text Limits")}</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">{t("headlineMin", "Headline Min")}</Label>
                <Input
                  type="number"
                  value={formData.headline_min_chars}
                  onChange={(e) => setFormData({ ...formData, headline_min_chars: parseInt(e.target.value) })} />
                
              </div>
              <div className="space-y-2">
                <Label className="text-xs">{t("headlineMax", "Headline Max")}</Label>
                <Input
                  type="number"
                  value={formData.headline_max_chars}
                  onChange={(e) => setFormData({ ...formData, headline_max_chars: parseInt(e.target.value) })} />
                
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">{t("bodyMin", "Body Min")}</Label>
                <Input
                  type="number"
                  value={formData.body_text_min_chars}
                  onChange={(e) => setFormData({ ...formData, body_text_min_chars: parseInt(e.target.value) })} />
                
              </div>
              <div className="space-y-2">
                <Label className="text-xs">{t("bodyMax", "Body Max")}</Label>
                <Input
                  type="number"
                  value={formData.body_text_max_chars}
                  onChange={(e) => setFormData({ ...formData, body_text_max_chars: parseInt(e.target.value) })} />
                
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">{t("ctaMax", "CTA Max")}</Label>
              <Input
                type="number"
                value={formData.cta_max_chars}
                onChange={(e) => setFormData({ ...formData, cta_max_chars: parseInt(e.target.value) })}
                className="w-1/2" />
              
            </div>
          </div>
          <div className="flex items-center justify-between">
            <Label>{t("globalSpecSet", "Global Spec Set")}</Label>
            <Switch checked={formData.is_global} onCheckedChange={(v) => setFormData({ ...formData, is_global: v })} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>{t("cancel", "Cancel")}</Button>
            <Button type="submit">{specSet ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>);

}
